import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { FormContainer } from '@/components/FormContainer'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import type { AddBusinessDialogViewModel } from './AddBusinessDialogViewModel'
import { BUSINESS_TYPES } from '../../../data/business-types'
import { masks } from '@/utils/input-mask-util'
import { MandatoryFieldsSubtitle } from '../../../components/MandatoryFieldsSubtitle'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'

interface AddBusinessDialogProps {
  vm: AddBusinessDialogViewModel
  testId?: string
}

export const AddBusinessDialog = observer(function AddBusinessDialog({
  vm,
  testId,
}: AddBusinessDialogProps) {
  const formId = useId()

  return (
    <ModalDialog
      isOpen={vm.modal.showing}
      onClose={vm.closeDialog}
      isDismissable
      sheetSizing="stretch"
      isUsingDialogScrolling
      size="lg"
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Add business</ModalDialogTitle>
        <ModalDialogDescription>
          <MandatoryFieldsSubtitle />
        </ModalDialogDescription>
      </ModalDialogHeader>

      <ModalDialogContent>
        <FormContainer
          id={formId}
          onSubmit={vm.save}
          testId={testId ? `${testId}-form` : undefined}
        >
          <SelectContainer
            model={vm}
            field="businessType"
            label={
              <FormFieldRequiredLabel label="How is your business set up?" />
            }
            items={BUSINESS_TYPES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            required
            testId={testId ? `${testId}-activity-code-select` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="businessName"
            label={<FormFieldRequiredLabel label="Business name" />}
            placeholder="e.g. Acme Freelance LLC"
            required
            testId={testId ? `${testId}-business-name-input` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="ein"
            mask={masks.ein}
            label={<FormFieldRequiredLabel label="EIN" />}
            placeholder="e.g. 12-3456789"
            required
            data-private
            testId={testId ? `${testId}-ein-input` : undefined}
          />
        </FormContainer>
      </ModalDialogContent>

      <ModalDialogFooter sticky bordered compact>
        <ModalDialogActions>
          <ButtonGroup stretch>
            <Button
              variant="outline"
              label="Cancel"
              onPress={vm.closeDialog}
              testId={testId ? `${testId}-cancel-button` : undefined}
            />
            <Button
              variant="default"
              label="Add business"
              type="submit"
              form={formId}
              testId={testId ? `${testId}-save-button` : undefined}
            />
          </ButtonGroup>
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
})
